<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
  type: { type: String, default: 'default' },
  message: { type: String, required: true },
  title: { type: String, default: '' },
  duration: { type: Number, default: 5000 }
})

const emit = defineEmits(['close'])
const visible = ref(false)
let timeoutId = null

const iconMap = {
  success: 'mdi-check-circle',
  error: 'mdi-close-circle',
  warning: 'mdi-alert',
  info: 'mdi-information',
  primary: 'mdi-information',
  default: 'mdi-information'
}

const currentIconClass = computed(() => iconMap[props.type] || iconMap.default)

const close = () => {
  visible.value = false
  setTimeout(() => emit('close'), 300)
}

onMounted(() => {
  // Pequeno delay para garantir que a animação de entrada ocorra
  requestAnimationFrame(() => {
    visible.value = true
  })

  if (props.duration > 0) {
    timeoutId = setTimeout(close, props.duration)
  }
})

onBeforeUnmount(() => {
  clearTimeout(timeoutId)
})
</script>

<template>
  <Transition name="toast-anim">
    <div
        v-if="visible"
        class="toast-notification"
        :class="[`type-${type}`]"
    >
      <div class="toast-icon-wrapper">
        <span :class="['mdi', currentIconClass, 'toast-icon']"></span>
      </div>

      <div class="toast-content">
        <p v-if="title" class="toast-title">{{ title }}</p>
        <p
          :class="['toast-message', { 'is-secondary': title }]"
          v-html="message"
        ></p>
      </div>

      <button
          @click="close"
          class="toast-close-btn"
          type="button"
      >
        <span class="mdi mdi-close close-icon"></span>
      </button>
    </div>
  </Transition>
</template>

<style scoped>
/* -- Layout Principal (Estilo Alert) -- */
.toast-notification {
  pointer-events: auto;
  display: flex;
  align-items: center; /* Centraliza verticalmente como no Alert original */
  gap: 12px; /* gap-3 */

  width: 100%;
  min-width: 300px;
  max-width: 450px;

  padding: 16px; /* p-4 */
  border-radius: 6px; /* rounded-md */
  background-color: #ffffff; /* bg-background-2 */

  font-family: "Roboto", sans-serif;

  /* shadow-lg do Tailwind */
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);

  /* Borda padrão transparente para não pular na transição */
  border: 1px solid transparent;

  box-sizing: border-box;
  transition: all 0.3s ease;
}

/* -- Cores (Borda Completa + Ícone) -- */
/* Success */
.type-success { border-color: #22c55e; } /* border-success */
.type-success .toast-icon { color: #22c55e; } /* text-success */

/* Error */
.type-error { border-color: #ef4444; } /* border-error */
.type-error .toast-icon { color: #ef4444; } /* text-error */

/* Warning */
.type-warning { border-color: #f59e0b; } /* border-warning */
.type-warning .toast-icon { color: #f59e0b; } /* text-warning */

/* Info */
.type-info { border-color: #3b82f6; } /* border-info */
.type-info .toast-icon { color: #3b82f6; } /* text-info */

/* Primary */
.type-primary { border-color: #6366f1; } /* border-primary */
.type-primary .toast-icon { color: #6366f1; } /* text-primary */

/* Default */
.type-default { border-color: #e5e7eb; } /* border-gray-2 */
.type-default .toast-icon { color: #6b7280; } /* text-font-3 */


/* -- Elementos Internos -- */
.toast-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
}

.toast-icon {
  font-size: 20px;
  line-height: 1;
}

.toast-content {
  display: flex;
  flex-direction: column;
  flex: 1; /* Ocupa todo espaço restante */
}

.toast-title {
  color: #1f2937; /* text-font-2 */
  font-size: 16px; /* text-md */
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
}

.toast-message {
  margin: 0;
  line-height: 1.4;
}

/* Se não tiver título, a mensagem é maior (text-md) */
.toast-message:not(.is-secondary) {
  color: #1f2937; /* text-font-2 */
  font-size: 16px; /* text-md */
}

/* Se tiver título, a mensagem é menor (text-sm) e mais clara */
.toast-message.is-secondary {
  font-size: 14px; /* text-sm-regular */
  color: #6b7280; /* text-font-4 */
}

.toast-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 9999px; /* rounded-full */
  border: none;
  background: transparent;
  color: #9ca3af; /* text-gray-2 */
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  margin-left: 4px;
}

.toast-close-btn:hover {
  background-color: #f3f4f6; /* hover:bg-gray-4 */
  color: #1f2937; /* hover:text-font-1 */
}

.close-icon {
  font-size: 20px; /* size-5 */
}

/* -- Animações Vue 2 -- */
/* Entrada */
.toast-anim-enter {
  opacity: 0;
  transform: translateX(100%);
}
.toast-anim-enter-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Saída */
.toast-anim-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
.toast-anim-leave-active {
  transition: all 0.3s ease-in;
}
</style>
